/**
 * templateEdits.js — story 224, HU-05 (DL14, RN-07.2): which linked units the user has EDITED.
 *
 * A unit is edited when its content differs from its baseline: how it was loaded, or how it was left by its last
 * materialization (DL25-a). The criterion is of CONTENT, never of position: it ignores the balance (job order, position in the
 * line, preferred machine), moving nodes, the code and name of the unit's own node, and the way the workflow is looked at.
 * Every edit belongs to the NEAREST linked unit that holds it (root included): a nested linked unit is a placeholder inside its
 * container (inserting or deleting it is an edit of the container; what is inside is the nested unit's own).
 * An edit undone by hand leaves the same canonical content, so it does not count (S189).
 *
 * Pure functions over the workflow model; nothing here mutates what it receives.
 */
import { TOLERATED_JOB_FIELDS } from '../operationLinkIntegrity'
import { findNodeDeep } from './templateMaterialization'

/** What of a job never counts as an edit: the balance (DL14) and the stamps of the index, which are not content. */
const NOT_CONTENT_JOB_FIELDS = [...TOLERATED_JOB_FIELDS, 'preferenceDevice', 'hasParts']
/** The operation-library (#130) intents that change the content of a job (RN-07.2): link, update, copy. */
const CONTENT_OPERATION_INTENTS = ['BIND', 'UPDATE', 'COPY']
const WORKFLOW_RESOURCE = 'WORKFLOW_RESOURCE'

const resourceType = (resource) => resource?.resourceType?.name || resource?.resourceType || resource?.type || ''
const isLoaded = (workflow) => Array.isArray(workflow?.nodes)

const connectionsOf = (node) => (node.nodeResults || [])
  .map((result) => [result.operationResult, result.nextNode, result.connectivity, result.eventType, result.eventValue].join('|'))
  .sort()

/** The assignments job <-> BOM component (rows of the workflow BOM) of every loaded job: they are the ROOT's, wherever the job is. */
const assignmentsOf = (level) => Object.fromEntries((level?.nodes || []).flatMap((node) => {
  const own = node.nodeType === 'JOB'
    ? [[node.id, (node.jobReference?.resources || []).filter((resource) => resourceType(resource) === WORKFLOW_RESOURCE)]]
    : []
  return [...own, ...Object.entries(assignmentsOf(node.subWorkflow))]
}).filter(([, resources]) => resources.length > 0))

/** The data of the job that is content: all but the balance; in a subworkflow also without the BOM assignments (RN-03.22). */
const jobContent = (jobReference = {}, isRoot) => {
  const content = {}
  Object.keys(jobReference || {}).forEach((field) => {
    if (NOT_CONTENT_JOB_FIELDS.includes(field)) {
      return
    }
    content[field] = field === 'resources'
      ? (jobReference.resources || []).filter((resource) => isRoot || resourceType(resource) !== WORKFLOW_RESOURCE)
      : jobReference[field]
  })
  return content
}

const canonicalNode = (node, peerNode, context) => {
  const base = { id: node.id, nodeType: node.nodeType, isFirst: Boolean(node.isFirst), connections: connectionsOf(node) }
  if (node.nodeType === 'JOB') {
    const intent = node.operationLink?.intent
    return {
      ...base,
      code: node.code,
      name: node.name || '',
      job: jobContent(node.jobReference, context.isRoot),
      operation: CONTENT_OPERATION_INTENTS.includes(intent) ? { intent, libraryNodeId: node.operationLink.libraryNodeId || '' } : {}
    }
  }
  // A nested LINKED unit is a placeholder: its content is its own. Its own node (code, name, position) is not content either.
  if (context.linkedIds.has(node.id)) {
    return { ...base, linked: true }
  }
  // An unlinked subworkflow is content of the unit; only what both sides have loaded can be compared (a lazy load is not an edit).
  const bothLoaded = isLoaded(node.subWorkflow) && isLoaded(peerNode?.subWorkflow)
  return {
    ...base,
    code: node.code,
    name: node.name || '',
    reference: node.subWorkflow?.id ?? '',
    content: bothLoaded ? canonicalLevel(node.subWorkflow, peerNode.subWorkflow, context) : {}
  }
}

const canonicalLevel = (level = {}, peerLevel = {}, context) => {
  const peers = new Map((peerLevel.nodes || []).map((node) => [node.id, node]))
  return {
    nodes: (level.nodes || []).map((node) => canonicalNode(node, peers.get(node.id), context)).sort((a, b) => String(a.id).localeCompare(String(b.id))),
    documents: (level.tempUUIDMap || []).map((entry) => entry.referenceUUID).sort()
  }
}

/** The ROOT's own content beyond its process (RN-07.2): BOM header, imported lines, pending #117 link and the assignments. */
const rootExtras = (workflow = {}) => ({
  bom: workflow.billOfMaterials || {},
  imported: Boolean(workflow.hasWorkflowResources),
  link: workflow.pendingBomLink || {},
  assignments: assignmentsOf(workflow)
})

/**
 * Whether the BOM or the documents of the workflow differ from the baseline (DL26: `bomAndDocumentsFromEditor`).
 * @returns {boolean}
 */
export const rootBomAndDocumentsEdited = (current = {}, baseline = {}) =>
  JSON.stringify(rootExtras(current)) !== JSON.stringify(rootExtras(baseline)) ||
  JSON.stringify((current.tempUUIDMap || []).map((entry) => entry.referenceUUID).sort()) !== JSON.stringify((baseline.tempUUIDMap || []).map((entry) => entry.referenceUUID).sort())

/**
 * Whether one unit has been edited (S187-S190).
 * @param {object} args
 * @param {object} args.current the unit as the editor holds it now (a workflow level: `nodes`, `tempUUIDMap`...)
 * @param {object} args.baseline the unit as it was loaded or as its last materialization left it
 * @param {boolean} args.isRoot the unit is the whole workflow (BOM, assignments and workflow documents count)
 * @param {Set<string>} args.linkedIds the node ids of the linked units: nested ones are placeholders
 * @returns {boolean} false when either side is not loaded: what was never loaded cannot have been edited
 */
export const isUnitEdited = ({ current, baseline, isRoot = false, linkedIds = new Set() }) => {
  if (!isLoaded(current) || !isLoaded(baseline)) {
    return false
  }
  const context = { isRoot, linkedIds }
  if (JSON.stringify(canonicalLevel(current, baseline, context)) !== JSON.stringify(canonicalLevel(baseline, current, context))) {
    return true
  }
  return isRoot && rootBomAndDocumentsEdited(current, baseline)
}

/**
 * The content of a unit in the editor: the workflow for the ROOT (`''`), the loaded `subWorkflow` of its node otherwise.
 * @returns {object} `{}` when the unit is not in the editor
 */
export const unitContentOf = (workflow = {}, unitId = '') => unitId === '' ? workflow : findNodeDeep(workflow.nodes || [], unitId).subWorkflow || {}

/**
 * Every unit of `unitIds` edited in `workflow`, in the order given (the ROOT, `''`, first).
 * @param {object} args
 * @param {object} args.workflow the editor's workflow
 * @param {string[]} args.unitIds ids of the candidate units (`''` = ROOT)
 * @param {function(string): object} args.baselineOf the baseline of a unit, `{}` when unknown
 * @param {Set<string>} args.linkedIds node ids of every linked unit (they are placeholders inside their container)
 * @returns {string[]}
 */
export const editedUnitIds = ({ workflow, unitIds, baselineOf, linkedIds }) => unitIds.filter((unitId) => isUnitEdited({
  current: unitContentOf(workflow, unitId),
  baseline: baselineOf(unitId),
  isRoot: unitId === '',
  linkedIds
}))
